import {
  WebDashboardEligibility,
  DataSourceType,
  DueInvoiceErrorType,
  FeatureFlag,
  GetSubscriptionInfo200Response,
  GetSubscriptions200Response,
  GetSubscriptionsPreCheckout200Response,
  PlanToJoin,
  SubscriptionBillingInterval,
  SubscriptionInvoice,
  SubscriptionPlanType,
  SubscriptionUser,
  UserSubscriptionInfo,
} from "@coeff/api";
import { PREMIUM_DATA_SOURCES } from "@coeff/utils";
import * as Sentry from "@sentry/react";
import { useFeatureGate } from "@statsig/react-bindings";
import { useQuery } from "@tanstack/react-query";
import { message } from "antd";
import querystring from "query-string";
import React, { ReactNode, useEffect, useState } from "react";
import { MapDispatchToProps, MapStateToProps, connect, useSelector } from "react-redux";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
  withRouter,
} from "react-router-dom";
import { QueryCacheKey } from "src/api";
import { AppState } from "src/store";
import styled from "styled-components";

import {
  AppThunkDispatch,
  checkout,
  getInvoices,
  getPreCheckoutData,
  getUserSubscription,
  getAllSubscriptions,
  updateSubscription,
} from "../../actions";
import { BillingActionResult } from "../../actions/billing";
import { fetchDataSources } from "../../api/dataSources";
import {
  Alert,
  CoeLogo,
  ConfirmBox,
  EventAttributionIframe,
  LoaderWithPerfTimings,
  Typography,
} from "../../components";
import { BContext, BillingPage, BillingPreCheckoutData } from "../../types";
import { getPricingSummary, useTrack } from "../../utils";
import { useApiContext } from "../../utils";

import { isPlanManager, isPlanMember, sortUsers } from "./billing.utils";
import { ContactAdminFullScreen } from "./components/ContactAdminFullScreen";
import { RequestLicenseFullScreen } from "./components/RequestLicenseFullScreen";
import { SelfServeCancelModal } from "./components/SelfServeCancelModal";
import { UpgradeToEnterpriseLimitDataSource } from "./components/UggradeToEnterpriseLimitDataSource";
import { UpgradeToEnterpriseFullScreen } from "./components/UpgradeToEnterpriseFullScreen";
import {
  isPaidPlanType,
  shouldBlockSubscriptionUpdateForPremiumEnforcement,
} from "./enterpriseBlocker.utils";
import { BillingHistory } from "./pages/BillingHistory";
import { Payment } from "./pages/Payment";
import { UpdatePayment } from "./pages/UpdatePayment";
import { ManageTeamV2 } from "./pagesV2/ManageTeam";
import { Members } from "./pagesV2/Members";
import { PlanCustomizerV2 } from "./pagesV2/PlanCustomizer";
import { PlanSelectorV2 } from "./pagesV2/PlanSelector";
import { SubscriptionInfoV2 } from "./pagesV2/SubscriptionInfo";
import { TeamV2 } from "./pagesV2/Team";
import { shouldStayOnPlanChooser } from "./planChooserSource.utils";
import {
  canPurchaseOwnPlan,
  canRequestLicenseWhileBillingHidden,
  getRequestLicenseGate,
  PURCHASE_OWN_PLAN_FROM,
  shouldShowRequestLicensePage,
} from "./planToJoin.utils";
import { Subscriptions } from "./Subscriptions";
import { shouldShowWebDashboardOffer } from "./webDashboardEligibility.utils";

const Wrap = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: auto;
  padding: 1rem;

  &.iframe {
    padding: 0px;
  }

  &.excel {
    padding: 24px 16px 16px 16px;
  }

  &.dashboard {
    margin: 0px;
    padding: 20px 30px;
  }
`;

const CoeLogoSmall = styled(CoeLogo)`
  width: 120px;
  margin: 10px 0 18px 0;
`;

const isExcel = querystring.parse(window.location.search).platform === "excel_addon";

const isIFrame = querystring.parse(window.location.search).isiframe === "true";

const userEmail = querystring.parse(window.location.search).email;

export const BillingContext = React.createContext<BContext | null>(null);

type ConnectDispatchProps = {
  checkout: (body: any) => Promise<BillingActionResult>;
  getPreCheckoutData: (subscriptionId?: string) => Promise<GetSubscriptionsPreCheckout200Response>;
  getUserSubscription: (subscriptionId?: string) => Promise<GetSubscriptionInfo200Response>;
  getAllSubscriptions: () => Promise<GetSubscriptions200Response>;
  getInvoices: (subscriptionId?: string) => void;
  updateSubscription: (body: any) => Promise<BillingActionResult>;
};

type ConnectProps = {
  coeffUserEmail: string;
  metadata: BillingPreCheckoutData;
  userSubscription?: GetSubscriptionInfo200Response | null;
  free_trial_expiration_dt?: Date;
  free_trial_total_days?: number;
  plan_type?: SubscriptionPlanType;
  due_invoices_count?: number;
  due_invoice_error_type?: DueInvoiceErrorType;
  invoices: Array<SubscriptionInvoice>;
  upgradeToken?: string;
  domains_in_domain_family: string[];

  subscriptions: GetSubscriptions200Response | null;
};

type Props = ConnectProps & ConnectDispatchProps;

const Billing: React.FC<Props> = ({
  checkout,
  coeffUserEmail,
  getPreCheckoutData,
  getUserSubscription,
  getInvoices,
  metadata: billingMetadata,
  userSubscription,
  updateSubscription,
  free_trial_expiration_dt,
  free_trial_total_days,
  plan_type,
  due_invoices_count,
  due_invoice_error_type,
  invoices,
  upgradeToken,
  domains_in_domain_family,
  getAllSubscriptions,
  subscriptions,
}) => {
  const track = useTrack();
  const { apiClient } = useApiContext();

  const { value: enableGmailDiscountTake50Coeff } = useFeatureGate(
    FeatureFlag.EnableGmailDiscountTake50coeff
  );

  // Gates the redesigned unified "Members" page (replaces ManageTeam/Team) behind Statsig.
  const { value: enableBillingMembersRedesign } = useFeatureGate(
    FeatureFlag.EnableBillingMembersRedesign
  );

  const { value: enableSelfServeAiDashboards } = useFeatureGate(
    FeatureFlag.EnableSelfServeAiDashboards
  );

  const [page, setPage] = useState<BillingPage>(
    userSubscription ? "subscription-info" : "plan-selector"
  );

  const [loading, setLoading] = useState<boolean>(true);

  const [selectedPlanType, setPlanType] = useState<SubscriptionPlanType | undefined>();

  const [selectedBillingInterval, setBillingInterval] =
    useState<SubscriptionBillingInterval>("annually");

  const [selectedDataSources, setDataSources] = useState<DataSourceType[]>([]);

  const [includeWebDashboards, setIncludeWebDashboards] = useState<boolean>(false);

  const [selectedUsers, setUsers] = useState<SubscriptionUser[]>([]);

  const [invitedUserEmails, setInvitedUserEmails] = useState<string[]>([]);

  const [checkoutSuccess, setCheckoutSuccess] = useState<boolean>(false);

  const [additionalSeats, setAdditionalSeats] = useState<number>(0);

  const [contactSupportInfoBox, setContactSupportInfoBox] = useState<{
    title: string;
    message?: ReactNode;
  }>();

  const [selfServeCancelOpen, setSelfServeCancelOpen] = useState(false);

  const currentUserEmail = coeffUserEmail ?? userEmail;
  const domain = currentUserEmail ? currentUserEmail.split("@").pop() : undefined;

  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  const premiumConnectorsUsed =
    billingMetadata?.all_data_source_types
      .filter(x => x.used_by_domain)
      .map(x => x.data_source_type)
      .filter(x => PREMIUM_DATA_SOURCES.includes(x)) || [];

  const history = useHistory();

  const location = useLocation();

  const { path } = useRouteMatch();

  const searchParams = new URLSearchParams(location.search);

  // ?from= as the page was opened (e.g. expansion_email_2 from a countdown email's Request License
  // button). A seatless user has no subscription, so init below redirects to /subscriptions with
  // the query string cleared before the Request License page shows (or, for a seatless admin
  // opening Email 1's Manage Licenses link, before they click through to Manage); keep the
  // original for those pages' events.
  const openedFrom = React.useRef(searchParams.get("from") ?? undefined).current;
  const eventFrom = searchParams.get("from") ?? openedFrom;

  // ENG-9319: the user chose "Or, purchase your own plan" -- on the Request License page, or via
  // the add-on gates' link -- so show the plan chooser instead where buying your own is allowed.
  const [choseOwnPlan, setChoseOwnPlan] = useState(openedFrom === PURCHASE_OWN_PLAN_FROM);

  const upgradeToPro = searchParams.get("upgrade_to_pro");

  const subscriptionId = searchParams.get("subscription_id") as string | undefined;

  // Shareable redirect link params
  const planParam = searchParams.get("plan") as SubscriptionPlanType | null;
  const intervalParam = searchParams.get("interval") as SubscriptionBillingInterval | null;
  const isValidPlanParam = planParam === "pro" || planParam === "starter";
  const isValidIntervalParam = intervalParam === "annually" || intervalParam === "monthly";

  // ENG-9207: opened on the plan chooser from an in-app upgrade CTA (e.g. the AI Dashboards
  // upgrade modal): stay on it, whatever subscription the user has.
  const stayOnPlanChooser =
    location.pathname === `${path}/plans` &&
    !subscriptionId &&
    shouldStayOnPlanChooser(searchParams.get("from"), enableSelfServeAiDashboards);

  const { data: dataSourceLimitBreached } = useQuery({
    queryKey: ["DataSourceLimitBreached", domain, DataSourceType.Quickbooks],
    queryFn: () =>
      apiClient.checkDataSourceConnectionLimit({
        data_source_type: DataSourceType.Quickbooks,
        domain: domain ?? "",
      }),
    enabled: Boolean(domain),
    select: response => response.data.data_source_limit_breached,
  });

  // Billing hidden (force_hide_billing_ui): only the Request License page can open, never the plan
  // chooser, prices or checkout (see canRequestLicenseWhileBillingHidden).
  const billingHiddenRequestOnly = canRequestLicenseWhileBillingHidden(
    clientFlags,
    currentUserEmail
  );
  if (clientFlags?.force_hide_billing_ui && !billingHiddenRequestOnly) {
    history.push("/dashboard");
  }

  useEffect(() => {
    const init = async () => {
      if (clientFlags?.force_hide_billing_ui && !billingHiddenRequestOnly) {
        return;
      }
      setLoading(true);
      await getPreCheckoutData(subscriptionId);

      const [response] = await Promise.all([
        getUserSubscription(subscriptionId),
        getAllSubscriptions(),
      ]);

      if (!response.subscription_details) {
        // Don't redirect to subscriptions if we have a valid plan param (shareable redirect link)
        // The redirect useEffect will handle it after loading is complete
        if (!isValidPlanParam && !stayOnPlanChooser) {
          history.push({
            pathname: `${path}/subscriptions`,
            search: "",
          });

          if (subscriptionId) {
            message.error("Not allowed to view this page");
          }
        }
      }

      setLoading(false);

      setBillingInterval(response.subscription_details?.billing_interval ?? "annually");

      if (!upgradeToPro) {
        track("billing_plan_chooser_viewed", { event_from: eventFrom });
      }

      window.opener?.postMessage(JSON.stringify({ type: "billing:loaded" }), "*");
    };

    init();

    document.title = "Plans & Billing - Coefficient Workspace";
  }, [subscriptionId]);

  const syncBilling = async () => {
    // getAllSubscriptions keeps the subscriptions payload (plan cards / seat totals on the
    // Members page) in step with the loaded subscription after seat changes.
    const [_, response] = await Promise.all([
      getPreCheckoutData(subscriptionId),
      getUserSubscription(subscriptionId),
      getAllSubscriptions(),
    ]);

    if (!response.subscription_details) {
      history.push({
        pathname: `${path}/subscriptions`,
        search: "",
      });
    }
  };

  useEffect(() => {
    if (!loading && !billingMetadata.plans.length) {
      Sentry.captureException(new Error("Session expired displayed"));
    }
  }, [loading, billingMetadata]);

  useEffect(() => {
    if (loading) {
      return;
    }

    // Handle shareable redirect links with plan param
    if (isValidPlanParam) {
      setPlanType(planParam);
      if (isValidIntervalParam) {
        setBillingInterval(intervalParam);
      }
      track("billing_plan_selected", {
        event_from: searchParams.get("from") ?? "redirect_link",
        selected_frequency: isValidIntervalParam ? intervalParam : "annually",
        selected_plan_type: planParam,
      });
      history.push({
        pathname: `${path}/customize`,
        search: window.location.search,
      });
    } else if (upgradeToPro) {
      // Legacy: internal redirect for paywall (email import refresh blocked)
      setPlanType("pro");
      track("billing_plan_selected", {
        event_from: "email_import_refresh_blocked",
        selected_frequency: selectedBillingInterval,
        selected_plan_type: "pro",
      });
      history.push({
        pathname: `${path}/customize`,
        search: window.location.search,
      });
    } else if (userSubscription && location.pathname === `${path}/plans`) {
      const searchParams = new URLSearchParams(window.location.search);

      if (
        shouldStayOnPlanChooser(
          searchParams.get("from"),
          enableSelfServeAiDashboards,
          Boolean(isManager)
        )
      ) {
        return;
      }
      history.push({
        pathname: `${path}/subscription`,
        search: window.location.search,
      });
    } else if (
      !userSubscription &&
      !subscriptions?.other_subscriptions?.length &&
      location.pathname !== `${path}/plans`
    ) {
      history.push({
        pathname: `${path}/plans`,
        search: window.location.search,
      });
    }
  }, [loading, userSubscription]);

  useEffect(() => {
    if (Boolean(checkoutSuccess)) {
      // we delay 10s before updating the props and analytics to give the iframe time to load GTM and other tracking scripts
      setTimeout(() => {
        setCheckoutSuccess(false);
      }, 10_000);
    }
  }, [checkoutSuccess]);

  const planData = billingMetadata?.plans.find(
    p => p.plan_type === selectedPlanType && p.billing_interval === selectedBillingInterval
  );

  const planMonthlyPrice: number =
    billingMetadata?.plans.find(
      p => p.plan_type === selectedPlanType && p.billing_interval === "monthly"
    )?.per_user_plan?.per_item_monthly_price_usd || 0;

  const planAnnualMonthlyPrice: number =
    billingMetadata?.plans.find(
      p => p.plan_type === selectedPlanType && p.billing_interval === "annually"
    )?.per_user_plan?.per_item_monthly_price_usd || 0;

  const webDashboardMonthlyPrice: number =
    billingMetadata?.plans.find(
      p => p.plan_type === selectedPlanType && p.billing_interval === "monthly"
    )?.web_dashboard_addon?.per_item_monthly_price_usd || 0;

  const webDashboardAnnualMonthlyPrice: number =
    billingMetadata?.plans.find(
      p => p.plan_type === selectedPlanType && p.billing_interval === "annually"
    )?.web_dashboard_addon?.per_item_monthly_price_usd || 0;

  // The AI Dashboards addon bundle toggle is only offered on plans that carry
  // the addon (Pro) when the feature flag is on, the user has no Pro license yet, and they have
  // used dashboards (the offer is hidden for everyone else).
  const webDashboardBundleEligible =
    shouldShowWebDashboardOffer(enableSelfServeAiDashboards, billingMetadata) &&
    billingMetadata?.web_dashboard_eligibility === WebDashboardEligibility.EligibleWithPlan;

  // Toggle defaults ON for bundle-eligible buyers; the ref ensures a deliberate toggle-off
  // is never overridden on subsequent eligibility re-evaluations.
  const webDashboardDefaultApplied = React.useRef(false);
  useEffect(() => {
    if (webDashboardBundleEligible && !webDashboardDefaultApplied.current) {
      webDashboardDefaultApplied.current = true;
      setIncludeWebDashboards(true);
    }
  }, [webDashboardBundleEligible]);

  // Fall back to the Pro plan's addon only when no plan type is selected yet (PlanSelector).
  // Once a plan is explicitly chosen (e.g. Starter), only that plan's addon data is used —
  // preventing Starter checkout from inheriting the Pro addon price.
  const webDashboardAddonAvailable = Boolean(
    planData?.web_dashboard_addon ??
      (!selectedPlanType
        ? billingMetadata?.plans.find(
            p => p.plan_type === "pro" && p.billing_interval === selectedBillingInterval
          )?.web_dashboard_addon
        : undefined)
  );

  // Self-serve AI Dashboards is single-seat only; multi-seat buyers go through sales.
  // Starter→Pro upgrades are allowed: the server's modify_subscription endpoint accepts
  // include_web_dashboards and handles the addon in a single Chargebee call.
  const isStarterPlanUpgrade = userSubscription?.subscription_details?.plan_type === "starter";

  const includeWebDashboardsInCheckout =
    webDashboardBundleEligible &&
    includeWebDashboards &&
    (!userSubscription?.subscription_details || isStarterPlanUpgrade) &&
    webDashboardAddonAvailable &&
    selectedUsers.length + additionalSeats + invitedUserEmails.length <= 1;

  const priceSummary = getPricingSummary(
    planData?.plan_type, // plan type
    selectedBillingInterval, // billing interval
    planMonthlyPrice, // monthly price for the plan (monthly interval)
    planAnnualMonthlyPrice, // monthly price for the plan (annual interval)
    planData?.per_additional_data_source_addon?.per_item_monthly_price_usd || 0, // addtional data source cost
    selectedUsers.length + additionalSeats + invitedUserEmails.length, // # of users
    selectedDataSources.length, // # of data souce
    webDashboardMonthlyPrice, // AI Dashboards addon price (flat per subscription, monthly interval)
    webDashboardAnnualMonthlyPrice, // AI Dashboards addon price (flat per subscription, annual interval)
    includeWebDashboardsInCheckout, // include AI Dashboards addon
    // for visual purposes only, we add a 50 percent discount for gmail users when viewing the price summary
    // this DOES NOT affect the actual price calculation during checkout
    enableGmailDiscountTake50Coeff && domain === "gmail.com"
      ? 50
      : billingMetadata?.coupon_discount_percentage
  );

  // Subscription information
  const usersInCurrentPlan: UserSubscriptionInfo[] = [];
  const otherUsersIndomain: UserSubscriptionInfo[] = [];

  const currentUserPlanManager = userSubscription?.users_from_domain.find(
    u => u.user.email === userSubscription?.subscription_details?.subscription_owner_email
  )?.user;

  userSubscription?.users_from_domain.forEach(user => {
    if (user.managed_by_user?.email === currentUserPlanManager?.email) {
      usersInCurrentPlan.push(user);
    } else {
      otherUsersIndomain.push(user);
    }
  });

  otherUsersIndomain.sort((a, b) => {
    return (b.num_imports || 0) - (a.num_imports || 0);
  });

  const basePath = path;

  const planManagers = (userSubscription?.users_from_domain || [])
    .filter(user => isPlanManager(user, userSubscription?.subscription_details))
    .sort(sortUsers);

  const planMembers = (userSubscription?.users_from_domain || [])
    .filter(
      user =>
        isPlanMember(user, userSubscription?.subscription_details) &&
        !isPlanManager(user, userSubscription?.subscription_details)
    )
    .concat(userSubscription?.invite_users || [])
    .sort(sortUsers);

  const isManager = [
    subscriptions?.user_subscription?.is_plan_manager
      ? subscriptions?.user_subscription?.user_subscription?.subscription_id
      : undefined,
    ...(subscriptions?.other_subscriptions?.map(subs => subs.subscription_id) || []),
  ].find(
    subId =>
      subId ===
      (subscriptionId || subscriptions?.user_subscription?.user_subscription?.subscription_id)
  );

  // A workspace admin (role ADMIN) can manage licenses alongside plan managers (ENG-8853); the
  // admin_emails client flag is the domain/domain-family admin list.
  const isAdmin = Boolean(
    currentUserEmail && (clientFlags?.admin_emails ?? []).includes(currentUserEmail)
  );

  const isFreeUser = !userSubscription?.users_from_domain.find(
    user => user.user.email === currentUserEmail
  )?.managed_by_user;

  // Admins and plan managers/owners manage billing for the domain, so they must never be locked
  // out of the billing page by the "unpaid user" gates below — otherwise an admin who removes
  // their own seat (dropping their personal plan_type to free) can no longer reach billing to
  // fix it.
  const canManageBilling = enableBillingMembersRedesign
    ? Boolean(clientFlags?.is_plan_manager) ||
      Boolean(currentUserEmail && (clientFlags?.admin_emails ?? []).includes(currentUserEmail))
    : false;

  // ENG-8735: a licensed paid-plan admin/plan-manager must never be locked out of managing seats
  // and licenses by the premium-connector checkout blocker (the Affinity/Procore recurrence). The
  // helper mirrors the same exemption the full-screen block below uses (isPaidPlanType +
  // canManageBilling), so the license-management path and the full-screen path agree.
  const enableSubscriptionUpdate: boolean =
    userSubscription?.subscription_details?.checkout_mode !== "contact_us" &&
    !shouldBlockSubscriptionUpdateForPremiumEnforcement(
      clientFlags,
      premiumConnectorsUsed.length,
      canManageBilling
    );

  // ENG-8924 / ENG-8973: the one self-checkout rule. A seatless user whose company already has a
  // plan to join (ENG-8920) gets the Request License page instead of the plan chooser; Plan
  // Managers and admins are never blocked, and without a plan to join everyone gets normal
  // self-serve checkout. ENG-9319: on a Pro team plan the page also offers "Or, purchase your own
  // plan", which shows the plan chooser; an Enterprise or Custom plan keeps it request-only. (The
  // server enforces the same rule on checkout.) Also the landing page for the countdown emails'
  // Request License button (ENG-8861). Decided once billing metadata has loaded, since whether
  // premium connectors are in use picks the gate.
  const requestLicenseGate = !loading
    ? getRequestLicenseGate(clientFlags, currentUserEmail, premiumConnectorsUsed.length)
    : undefined;
  if (shouldShowRequestLicensePage(requestLicenseGate, choseOwnPlan) && clientFlags) {
    return (
      <RequestLicenseFullScreen
        gateType={requestLicenseGate.gateType}
        planToJoin={requestLicenseGate.planToJoin as PlanToJoin}
        premiumConnectorsUsed={premiumConnectorsUsed}
        eventFrom={eventFrom}
        onPurchaseOwnPlan={
          canPurchaseOwnPlan(requestLicenseGate) ? () => setChoseOwnPlan(true) : undefined
        }
      />
    );
  }

  if (clientFlags?.force_hide_billing_ui) {
    // Request-only access and it turned out not to apply (e.g. the premium gate decided and has no
    // plan to join): nothing else on this page is for them.
    if (!loading) {
      history.push("/dashboard");
    }
    return null;
  }

  if (
    clientFlags?.enforce_billing_checkout_enterprise_blocker &&
    premiumConnectorsUsed.length > 0 &&
    !isPaidPlanType(clientFlags?.plan_type) &&
    !canManageBilling
  ) {
    return clientFlags?.domain_has_enterprise_subscription &&
      (clientFlags?.admin_emails ?? []).length > 0 ? (
      <ContactAdminFullScreen premiumConnectorsUsed={premiumConnectorsUsed} />
    ) : (
      <UpgradeToEnterpriseFullScreen premiumConnectorsUsed={premiumConnectorsUsed} />
    );
  }

  if (
    clientFlags?.enforce_billing_checkout_enterprise_blocker_for_limiting_data_source_connections &&
    dataSourceLimitBreached &&
    !isPaidPlanType(clientFlags?.plan_type) &&
    !canManageBilling
  ) {
    return <UpgradeToEnterpriseLimitDataSource />;
  }

  return (
    <BillingContext.Provider
      value={{
        additionalSeats,
        eventFrom,
        basePath,
        billingMetadata,
        checkout,
        checkoutSuccess,
        currentUserEmail,
        enableSubscriptionUpdate,
        premiumConnectorsUsed,
        domain,
        free_trial_expiration_dt,
        free_trial_total_days,
        getUserSubscription,
        getInvoices,
        page,
        plan_type,
        priceSummary,
        selectedPlanType,
        selectedBillingInterval,
        selectedDataSources,
        selectedUsers,
        includeWebDashboards: includeWebDashboardsInCheckout,
        setIncludeWebDashboards,
        setCheckoutSuccess,
        setContactSupportInfoBox,
        setPage,
        setPlanType,
        setBillingInterval,
        setDataSources,
        setAdditionalSeats,
        setUsers,
        // @ts-ignore - doesn't account for error object coming from reducer action
        updateSubscription,
        userSubscription,
        due_invoices_count,
        due_invoice_error_type,
        usersInCurrentPlan,
        otherUsersIndomain,
        currentUserPlanManager,
        invoices,
        upgradeToken,
        domains_in_domain_family,
        invitedUserEmails,
        setInvitedUserEmails,
        syncBilling,
        subscriptions,
        planManagers,
        planMembers,
        isFreeUser,
        isManager: !!isManager,
        isAdmin,
        openSelfServeCancelModal: () => setSelfServeCancelOpen(true),
      }}
    >
      {loading ? (
        <div style={{ margin: "7rem auto 2rem", textAlign: "center" }}>
          <LoaderWithPerfTimings name="Billing" size="large" />
        </div>
      ) : billingMetadata.plans.length > 0 ? (
        <>
          <Wrap
            className={`${isIFrame ? "iframe" : ""} ${isExcel ? "excel" : ""} ${
              location.pathname.includes("dashboard") ? "dashboard" : ""
            }`}
          >
            {Boolean(checkoutSuccess) && (
              <EventAttributionIframe
                email={currentUserEmail}
                event_name="Purchase"
                event_props={{
                  plan_type: userSubscription?.subscription_details?.plan_type,
                  currency: "USD",
                  amount: userSubscription?.subscription_details?.recurring_amount_usd,
                  billing_interval: userSubscription?.subscription_details?.billing_interval,
                }}
              />
            )}
            {!isIFrame &&
              !location.pathname.includes("dashboard") &&
              location.pathname !== `${path}/update-payment` && <CoeLogoSmall />}
            <Switch>
              <Route exact path={`${path}`}>
                <Redirect
                  to={{
                    pathname: `${path}/plans`,
                    search: window.location.search,
                  }}
                />
              </Route>
              <Route path={`${path}/plans`}>
                <PlanSelectorV2 />
              </Route>
              <Route path={`${path}/customize`}>
                <PlanCustomizerV2 />
              </Route>
              <Route path={`${path}/payment`}>
                <Payment />
              </Route>
              <Route path={`${path}/subscriptions`} exact>
                {enableBillingMembersRedesign ? <Members /> : <Subscriptions />}
              </Route>
              <Route path={`${path}/subscription`}>
                <SubscriptionInfoV2 />
              </Route>
              <Route path={`${path}/billing`}>
                <BillingHistory />
              </Route>
              <Route path={`${path}/team/manage`}>
                {enableBillingMembersRedesign ? <Members /> : <ManageTeamV2 />}
              </Route>
              <Route path={`${path}/team`}>
                {enableBillingMembersRedesign ? <Members /> : <TeamV2 />}
              </Route>
              <Route path={`${path}/update-payment`}>
                <UpdatePayment />
              </Route>
            </Switch>
          </Wrap>

          <ConfirmBox
            title={contactSupportInfoBox?.title}
            visible={Boolean(contactSupportInfoBox)}
            onOk={() => setContactSupportInfoBox(undefined)}
            okText="Got it"
            width={400}
            okButtonProps={{
              style: { marginLeft: "auto" },
            }}
          >
            {contactSupportInfoBox?.message ? (
              contactSupportInfoBox.message
            ) : (
              <>
                Please contact us at{" "}
                <a href="mailto:support@coefficient.io" target="_blank">
                  support@coefficient.io
                </a>
                <br />
                to make changes
              </>
            )}
          </ConfirmBox>

          <SelfServeCancelModal
            visible={selfServeCancelOpen}
            onClose={() => setSelfServeCancelOpen(false)}
          />
        </>
      ) : (
        <div>
          <Alert
            message={
              <Typography variant="body1" fontWeight="bold">
                Session expired
              </Typography>
            }
            description={
              <a href="/dashboard/billing" target="_blank" style={{ fontWeight: "bold" }}>
                Back to Plans & Billing
              </a>
            }
            type="error"
            style={{ margin: "1rem", padding: 16 }}
          />
        </div>
      )}
    </BillingContext.Provider>
  );
};

const mapDispatchToProps: MapDispatchToProps<ConnectDispatchProps, unknown> = (
  dispatch: AppThunkDispatch
) => ({
  getPreCheckoutData: (subscriptionId?: string) => dispatch(getPreCheckoutData(subscriptionId)),
  getUserSubscription: (subscriptionId?: string) => dispatch(getUserSubscription(subscriptionId)),
  getAllSubscriptions: () => dispatch(getAllSubscriptions()),
  getInvoices: (subscriptionId?: string) => dispatch(getInvoices(subscriptionId)),
  updateSubscription: body => dispatch(updateSubscription(body)),
  checkout: body => dispatch(checkout(body)),
});

const mapStateToProps: MapStateToProps<ConnectProps, unknown, AppState> = ({
  billing: { metadata, userSubscription, invoices, subscriptions },
  app: {
    coeffUserEmail,
    free_trial_expiration_dt,
    free_trial_total_days,
    plan_type,
    due_invoices_count,
    due_invoice_error_type,
    upgradeToken,
    domains_in_domain_family,
  },
}) => ({
  metadata,
  userSubscription,
  coeffUserEmail,
  free_trial_expiration_dt,
  free_trial_total_days,
  plan_type,
  due_invoices_count,
  due_invoice_error_type,
  invoices,
  upgradeToken,
  domains_in_domain_family,
  subscriptions,
});

const route = withRouter(connect(mapStateToProps, mapDispatchToProps)(Billing));

export { route as Billing };
